/**
 * Структурированное логирование фронтенда.
 *
 * <p>В development пишем читаемую строку плюс объект, в остальных окружениях — одну JSON-строку:
 * её забирает fluent-bit из логов контейнера. Последние записи дублируются в localStorage —
 * это единственный способ посмотреть, что происходило в браузере пользователя, когда сеть
 * уже недоступна.</p>
 */

export const LOG_LEVELS = {
    ERROR: 'ERROR',
    WARN: 'WARN',
    INFO: 'INFO',
    DEBUG: 'DEBUG',
} as const;

export type LogLevel = typeof LOG_LEVELS[keyof typeof LOG_LEVELS];

/** Произвольные поля записи: их дописывает вызывающий код. */
export type LogData = Record<string, unknown>;

export interface LogRecord extends LogData {
    '@timestamp': string;
    level: LogLevel;
    message: string;
    application: string;
    environment: string;
    sessionId: string;
    userId: string | null;
    userAgent: string;
    url: string;
}

/** Сколько последних записей держим в браузере: больше — и localStorage заполняется мусором. */
const STORED_LOGS_LIMIT = 100;
const STORAGE_KEY = 'frontend_logs';

export class Logger {
    private readonly environment: string;
    private readonly appName: string;
    private readonly sessionId: string;
    private userId: string | null;

    constructor() {
        this.environment = process.env.NODE_ENV || 'development';
        this.appName = 'clothes-frontend';
        this.userId = null;
        this.sessionId = Logger.generateSessionId();
    }

    private static generateSessionId(): string {
        return 'session_' + Math.random().toString(36).slice(2, 11);
    }

    setUserId(userId: string | null): void {
        this.userId = userId;
    }

    getLogData(level: LogLevel, message: string, data: LogData = {}): LogRecord {
        return {
            '@timestamp': new Date().toISOString(),
            level,
            message,
            application: this.appName,
            environment: this.environment,
            sessionId: this.sessionId,
            userId: this.userId,
            userAgent: navigator.userAgent,
            url: window.location.href,
            ...data,
        };
    }

    log(level: LogLevel, message: string, data: LogData = {}): void {
        const logData = this.getLogData(level, message, data);

        if (this.environment === 'development') {
            console.log(`[${level}] ${message}`, logData);
        } else {
            console.log(JSON.stringify(logData));
        }

        this.storeInLocalStorage(logData);
    }

    error(message: string, data: LogData = {}): void {
        this.log(LOG_LEVELS.ERROR, message, data);
    }

    warn(message: string, data: LogData = {}): void {
        this.log(LOG_LEVELS.WARN, message, data);
    }

    info(message: string, data: LogData = {}): void {
        this.log(LOG_LEVELS.INFO, message, data);
    }

    debug(message: string, data: LogData = {}): void {
        if (this.environment === 'development') {
            this.log(LOG_LEVELS.DEBUG, message, data);
        }
    }

    /** Действие пользователя для разбора инцидентов. */
    audit(action: string, details: LogData = {}): void {
        this.info(`AUDIT: ${action}`, {
            log_type: 'audit',
            action,
            ...details,
        });
    }

    /** Обращение к API: метод, адрес, код и длительность. */
    apiCall(method: string, url: string, status: number, duration: number, details: LogData = {}): void {
        this.info(`API ${method} ${url}`, {
            log_type: 'api',
            method,
            url,
            status,
            duration,
            ...details,
        });
    }

    /** Переход между экранами. */
    navigation(from: string, to: string, details: LogData = {}): void {
        this.info(`Navigation: ${from} -> ${to}`, {
            log_type: 'navigation',
            from,
            to,
            ...details,
        });
    }

    errorWithStack(message: string, error: Error, data: LogData = {}): void {
        this.error(message, {
            ...data,
            error: {
                name: error?.name,
                message: error?.message,
                stack: error?.stack,
            },
        });
    }

    /**
     * Хранилище может быть недоступно (приватное окно, запрет данных сайта) и бросать на
     * каждом обращении. Логирование не имеет права ломать экран, поэтому любая ошибка здесь
     * молча проглатывается.
     */
    storeInLocalStorage(logData: LogRecord): void {
        try {
            const logs: LogRecord[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
            logs.push(logData);

            if (logs.length > STORED_LOGS_LIMIT) {
                logs.splice(0, logs.length - STORED_LOGS_LIMIT);
            }

            localStorage.setItem(STORAGE_KEY, JSON.stringify(logs));
        } catch {
            // хранилище недоступно — записи просто не сохраняем
        }
    }

    getStoredLogs(): LogRecord[] {
        try {
            return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
        } catch {
            return [];
        }
    }

    clearStoredLogs(): void {
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch {
            // хранилище недоступно — чистить нечего
        }
    }
}

const logger = new Logger();

export default logger;
